import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Chip, CircularProgress, useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon, LocalShipping as SupplierIcon,
  Edit as EditIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import supplierService from '../../../services/supplierService';
import type { Supplier, SupplierType } from '../../../types/venue';

const SUPPLIER_TYPES: SupplierType[] = ['DISTRIBUTOR', 'DIRECT_IMPORT', 'AUCTION', 'MERCHANT', 'OTHER'];

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [items, setItems] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });

  const fetchItems = useCallback(async (query: string, type: string, page: number, pageSize: number) => {
    setLoading(true);
    try {
      const result = await supplierService.getAllSuppliers(
        page + 1, pageSize, 'name', 'asc',
        query || undefined,
        type || undefined,
      );
      setItems(result.data);
      setPagination(prev => ({
        ...prev,
        page,
        pageSize,
        totalItems: result.pagination.totalItems,
        totalPages: result.pagination.totalPages,
      }));
    } catch (err) {
      console.error('Failed to load suppliers', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(
    () => debounce((query: string, type: string, page: number, pageSize: number) => {
      fetchItems(query, type, page, pageSize);
    }, 300),
    [fetchItems],
  );

  useEffect(() => {
    debouncedFetch(searchQuery, typeFilter, pagination.page, pagination.pageSize);
    return () => debouncedFetch.cancel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, typeFilter, pagination.page, pagination.pageSize]);

  const handleDelete = async (e: React.MouseEvent, supplier: Supplier) => {
    e.stopPropagation();
    if (!window.confirm(`Deactivate "${supplier.name}"?`)) return;
    try {
      await supplierService.deleteSupplier(supplier._id);
      fetchItems(searchQuery, typeFilter, pagination.page, pagination.pageSize);
    } catch (err) {
      console.error('Failed to delete supplier', err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SupplierIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Suppliers</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/suppliers/create')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            New Supplier
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <TextField
            size="small"
            placeholder="Search suppliers…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPagination(prev => ({ ...prev, page: 0 })); }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={e => { setTypeFilter(e.target.value); setPagination(prev => ({ ...prev, page: 0 })); }}
            >
              <MenuItem value="">All types</MenuItem>
              {SUPPLIER_TYPES.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Type</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Country</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Account Ref</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Contact</TableCell>
                <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <SupplierIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">No suppliers found.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(supplier => (
                  <TableRow
                    key={supplier._id}
                    hover
                    onClick={() => navigate(`/admin/suppliers/${supplier._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{supplier.name}</Typography>
                      {supplier.shortName && (
                        <Typography variant="caption" color="text.secondary">{supplier.shortName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {supplier.type.replace('_', ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{supplier.country || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {supplier.accountReference || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{supplier.contactName || '—'}</Typography>
                      {supplier.contactEmail && (
                        <Typography variant="caption" color="text.secondary">{supplier.contactEmail}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={supplier.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={supplier.active ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => navigate(`/admin/suppliers/edit/${supplier._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={e => handleDelete(e, supplier)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.totalItems}
          page={pagination.page}
          rowsPerPage={pagination.pageSize}
          onPageChange={(_e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          onRowsPerPageChange={e => setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default SupplierList;
